import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Swal from 'sweetalert2'
import CertificationsService from '@/services/CertificationsService'
import TrainingsService from '@/services/TrainingsService'
import UserService2 from '@/services/UserService2'
import useAssignedTrainers from './useAssignedTrainers'

const formatDate = value => value ? `${value}`.substring(0, 10) : '—'

export default function useTrainingDetailsPage() {
	const { t } = useTranslation()
	const { id } = useParams()
	const navigate = useNavigate()
	const [training, setTraining] = useState(null)
	const [certificationOptions, setCertificationOptions] = useState([])
	const [loading, setLoading] = useState(true)

	const refreshTraining = useCallback(async () => {
		const result = await TrainingsService.getById(id)
		if (!result.error && result.data) {
			setTraining(result.data)
		}
		return result
	}, [id])

	const getTrainersForTraining = useCallback(
		options => UserService2.getTrainersForTraining(options),
		[]
	)
	const updateTraining = useCallback(
		(trainingId, payload) => TrainingsService.update(trainingId, payload),
		[]
	)

	const {
		assignedTrainerRows,
		addAssignedTrainer,
		persistAssignedTrainers,
		removeAssignedTrainer,
		refreshTrainersForModal,
		syncTrainerDirectory,
	} = useAssignedTrainers({
		training,
		trainingId: id,
		getTrainersForTraining,
		updateTraining,
		refreshTraining,
	})

	const loadTraining = useCallback(async () => {
		setLoading(true)
		const [trainingResult, certificationsResult] = await Promise.all([
			TrainingsService.getById(id),
			CertificationsService.getAll({ limit: 999, sortColumn: 'name', sortDirection: 'asc' }),
		])

		if (trainingResult.error || !trainingResult.data) {
			await Swal.fire(
				t('notifications.errorTitle'),
				trainingResult.error || t('trainings.details.messages.loadFailed'),
				'error'
			)
			navigate('/centrumSzkolen/szkolenia')
			return
		}

		if (certificationsResult.error) {
			await Swal.fire(
				t('notifications.errorTitle'),
				certificationsResult.error || t('trainings.details.messages.loadFailed'),
				'error'
			)
			navigate('/centrumSzkolen/szkolenia')
			return
		}

		const trainingData = trainingResult.data
		setTraining(trainingData)
		setCertificationOptions(
			(Array.isArray(certificationsResult.data) ? certificationsResult.data : []).map(item => ({
				value: item._id,
				label: item.name,
			}))
		)
		await syncTrainerDirectory(trainingData)
		setLoading(false)
	}, [id, navigate, syncTrainerDirectory, t])

	useEffect(() => {
		if (id) {
			loadTraining()
		}
	}, [id, loadTraining])

	const statusLabel = useMemo(() => {
		if (!training?.status) return '—'
		return t(`trainings.list.statusOptions.${training.status}`, { defaultValue: training.status })
	}, [t, training?.status])

	const certificationMap = useMemo(() => new Map(
		(Array.isArray(certificationOptions) ? certificationOptions : []).map(item => [item.value, item.label || '—'])
	), [certificationOptions])

	const certificationNames = useMemo(() => {
		const certificates = Array.isArray(training?.certificates) ? training.certificates : []
		if (certificates.length) {
			return certificates
				.map(item => item.certificateName || certificationMap.get(item.certificateId) || item.certificateId)
				.filter(Boolean)
		}

		if (!Array.isArray(training?.requiredCertificationIds)) return []
		return training.requiredCertificationIds.map(item => certificationMap.get(item) || item).filter(Boolean)
	}, [certificationMap, training?.certificates, training?.requiredCertificationIds])

	const uploadTrainingFile = useCallback(async (file, onUploadProgress) => {
		const result = await TrainingsService.uploadTrainingFile(id, file, onUploadProgress)

		if (result.success) {
			await refreshTraining()
		}

		return result
	}, [id, refreshTraining])

	const downloadTrainingFile = useCallback(
		fileUuid => TrainingsService.downloadTrainingFile(id, fileUuid),
		[id]
	)

	const deleteTrainingFile = useCallback(async fileUuid => {
		const result = await TrainingsService.deleteTrainingFile(id, fileUuid)

		if (result.success) {
			await refreshTraining()
		}

		return result
	}, [id, refreshTraining])

	return {
		addAssignedTrainer,
		assignedTrainerRows,
		persistAssignedTrainers,
		certificationNames,
		certificationOptions,
		createdDate: formatDate(training?.created),
		loading,
		refreshTraining,
		refreshTrainersForModal,
		removeAssignedTrainer,
		statusLabel,
		training,
		uploadTrainingFile,
		downloadTrainingFile,
		deleteTrainingFile,
	}
}
